import React from 'react';
import ShowCertificateIcon from '@assets/icons/table/show-certificate.svg';
import Status from '@components/table/status';
import { CertificateWrapper } from '@components/table/styled.tsx';
import { getStatusType } from '@components/table/utils.tsx';
import { BodySmSm, BodySmSmMedium } from '@constants/text.ts';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import {
    SHARED_MY_LEARNING_METHOD_ADAPTIVE,
    SHARED_MY_LEARNING_METHOD_FINAL_QUIZ,
    SHARED_MY_LEARNING_METHOD_NO_FINAL_QUIZ,
    SHARED_MY_LEARNING_METHOD_ONBOARD_TASK,
    SHARED_MY_LEARNING_METHOD_STANDARD,
    SHARED_MY_LEARNING_OF,
} from '@routes/profile-bar/___pages___/my-results/ui/tables/my-learning/constants.ts';
import { translateItem } from '@utils/translations.ts';
import isNil from 'lodash/isNil';

import type { CompletionMethod, UserLearning } from '../../../api/results.api.ts';
import { COMPLETION_METHOD, DEFAULT_FINAL_QUIZ_MAX_ATTEMPTS } from './constants.ts';
import { PathwayCellWrapper, PathwayMeta } from './styled.tsx';
import type { CertificateCellProps, MyLearningRow, PathwayColumnData } from './types.ts';

export const mapCompletionMethodLines = (method: CompletionMethod): string[] => {
    switch (method) {
        case COMPLETION_METHOD.FinalQuiz:
            return [
                translateItem(SHARED_MY_LEARNING_METHOD_STANDARD),
                translateItem(SHARED_MY_LEARNING_METHOD_FINAL_QUIZ),
            ];
        case COMPLETION_METHOD.Standard:
            return [
                translateItem(SHARED_MY_LEARNING_METHOD_STANDARD),
                translateItem(SHARED_MY_LEARNING_METHOD_NO_FINAL_QUIZ),
            ];
        case COMPLETION_METHOD.AdaptiveQuiz:
            return [
                translateItem(SHARED_MY_LEARNING_METHOD_ADAPTIVE),
                translateItem(SHARED_MY_LEARNING_METHOD_NO_FINAL_QUIZ),
            ];
        case COMPLETION_METHOD.AdaptiveQuizWithFinalQuizRequired:
            return [translateItem(SHARED_MY_LEARNING_METHOD_ADAPTIVE), translateItem(msg`Final quiz required`)];
        case COMPLETION_METHOD.OnboardTask:
            return [translateItem(SHARED_MY_LEARNING_METHOD_ONBOARD_TASK)];
        case COMPLETION_METHOD.Exemption:
            return [translateItem(msg`Exemption`)];
        default:
            return ['-'];
    }
};

export const mapCompletionMethodLabel = (method: CompletionMethod): string =>
    mapCompletionMethodLines(method).join('\n');

export const CompletionMethodCell: React.FC<{ method: CompletionMethod }> = ({ method }) => (
    <PathwayCellWrapper>
        {mapCompletionMethodLines(method).map((line) => (
            <BodySmSm key={line}>{line}</BodySmSm>
        ))}
    </PathwayCellWrapper>
);

export const formatAttempt = (attempt: number | null, maxAttempts: number | null): string => {
    if (isNil(attempt) || attempt <= 0) {
        return '-';
    }

    const resolvedMaxAttempts = maxAttempts && maxAttempts > 0 ? maxAttempts : DEFAULT_FINAL_QUIZ_MAX_ATTEMPTS;
    const ofLabel = translateItem(SHARED_MY_LEARNING_OF);

    return `${attempt} ${ofLabel} ${resolvedMaxAttempts}`;
};

export const formatPathwayTypeCategory = (pathwayType: string, pathwayCategory: string): string =>
    [pathwayType, pathwayCategory].filter(Boolean).join(' - ') || '-';

export const PathwayCell: React.FC<PathwayColumnData> = ({ title, version, pathwayType, pathwayCategory }) => {
    const { t } = useLingui();

    return (
        <PathwayCellWrapper>
            <BodySmSmMedium>{title}</BodySmSmMedium>
            <PathwayMeta>{version ? t`Ver: ${version}` : t`Ver: -`}</PathwayMeta>
            <PathwayMeta>{formatPathwayTypeCategory(pathwayType, pathwayCategory)}</PathwayMeta>
        </PathwayCellWrapper>
    );
};

export const CertificateCell: React.FC<CertificateCellProps> = ({ resultId, hasCertificate, onOpen }) => {
    if (!hasCertificate) {
        return <>-</>;
    }

    return (
        <CertificateWrapper onClick={() => onOpen(resultId)}>
            <ShowCertificateIcon />
        </CertificateWrapper>
    );
};

export const StatusCell: React.FC<{ status: string }> = ({ status }) => (
    <Status type={getStatusType(status)} label={status} />
);

export const mapUserLearningToRow = (item: UserLearning): MyLearningRow => ({
    key: String(item.resultId),
    title: {
        title: item.title,
        version: item.pathwayVersion,
        pathwayType: item.pathwayType,
        pathwayCategory: item.pathwayCategory,
    },
    stateUpdateDateUTC: item.stateUpdateDateUTC,
    rank: item.rank,
    locationName: item.locationName,
    scorePercent: item.scorePercent,
    finalQuizAttemptNumber: {
        attempt: item.finalQuizAttemptNumber,
        maxAttempts: item.finalQuizMaxAttempts,
    },
    status: item.status,
    completionMethod: item.completionMethod,
    hasCertificate: {
        resultId: item.resultId,
        hasCertificate: item.hasCertificate,
    },
});

export const mapTableFiltersToRequestFilters = (
    tableFilters: Record<string, string[]> | null,
    filterRequestMap: Record<string, string>,
): Record<string, string[]> | null => {
    if (!tableFilters) {
        return null;
    }

    const mapped = Object.entries(tableFilters).reduce<Record<string, string[]>>((acc, [key, values]) => {
        const apiKey = filterRequestMap[key] ?? key;
        if (values.length) {
            acc[apiKey] = values;
        }
        return acc;
    }, {});

    return Object.keys(mapped).length ? mapped : null;
};
